import Image from 'next/image';
import Link from 'next/link';
import { DateTime } from 'luxon';
import { titleCase } from 'title-case';

import { entryToLink } from '../lib/copyRendering';
import { Text } from './Text';
import articleAuthor from '../assets/article-author.svg';
import { DateTimeDisplay } from './DateTimeDisplay';
import { ArticleCardFragment } from './ArticleCard.generated';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { Fragment } from 'react';

interface ArticleCard {
  article: ArticleCardFragment;
}

export const ArticleCard: React.FC<ArticleCard> = ({ article }) => (
  <Link
    href={entryToLink(article)}
    className="rounded-lg h-full flex flex-col bg-white drop-shadow-lg hover:opacity-90 hover:bg-gray-50"
  >
    {article.image?.url && (
      <Image
        src={article.image.url}
        width={484}
        height={225}
        alt={article.image.description ?? 'Featured Plan'}
        className="rounded-t-lg w-full"
      />
    )}
    <div className="px-4 py-5 sm:px-6 flex-1">
      <div className="text-sm text-bright-purple truncate">
        {article.categoriesCollection?.items
          .map((item) => item?.name)
          .join(', ')}
      </div>
      <Text.H3 className="!text-lg md:!text-xl mb-3">
        {titleCase(article.title ?? '')}
      </Text.H3>
      <div className="text-gray-500 text-ellipsis line-clamp-4">
        {article.description
          ? documentToPlainTextString(article.description.json)
          : documentToPlainTextString(article.content?.json, '\n')
              .split('\n')
              .slice(0, 3)
              .map((paragraph, index) => (
                <Fragment key={index}>
                  {paragraph}
                  <br></br>
                </Fragment>
              ))}
      </div>
    </div>
    <div className="px-4 py-5 sm:px-6 flex items-center">
      <Image src={articleAuthor} alt="HealthPlans of NC" className="mr-3" />
      <div>
        <p className="text-sm font-medium">{article.author}</p>
        <p className="text-sm text-gray-500">
          {article.publishedDate && (
            <DateTimeDisplay
              dateString={article.publishedDate}
              format={DateTime.DATE_MED}
            />
          )}
        </p>
      </div>
    </div>
  </Link>
);
